import React, { useState } from "react";
import {
  Card,
  CardContent,
  Typography,
  Box,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  Chip,
  Avatar,
} from "@mui/material";
import {
  LibraryBooks as LibraryBooksIcon,
  MoreVert as MoreVertIcon,
  Delete as DeleteIcon,
  Share as ShareIcon,
  Edit as EditIcon,
  Visibility as VisibilityIcon,
  AdminPanelSettings as AdminIcon,
} from "@mui/icons-material";
import { collectionsApi } from "../services/rag-service";
import { toast } from "react-toastify";
import ShareDialog from "./ShareDialog";
import { useTranslation } from "react-i18next";
import { getIntlLocale } from "../utils/language";

const CollectionCard = ({ collection, onClick, onDelete }) => {
  const { t, i18n } = useTranslation();
  const {
    id,
    name,
    description,
    document_count,
    created_at,
    is_shared,
    access_level,
    project_id,
  } = collection;
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  // Share dialog states
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [isSharing, setIsSharing] = useState(false);
  const [sharingError, setSharingError] = useState("");

  // Format date for display
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const lang = i18n.language || "en";
    return date.toLocaleDateString(getIntlLocale(lang), {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const handleMenuOpen = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = (event) => {
    if (event) event.stopPropagation();
    setAnchorEl(null);
  };

  const handleDelete = (event) => {
    event.stopPropagation();
    handleMenuClose();
    if (onDelete) onDelete(id, name, access_level);
  };

  const handleShareClick = (event) => {
    event.stopPropagation();
    handleMenuClose();
    setShareDialogOpen(true);
    setSharingError("");
  };

  const handleShareDialogClose = () => {
    setShareDialogOpen(false);
  };

  const handleShareSubmit = async (email, selectedAccessLevel) => {
    setIsSharing(true);
    setSharingError("");

    try {
      await collectionsApi.shareCollection(id, {
        user: email,
        role: selectedAccessLevel,
      });

      // Success
      toast.success(
        t("collections.sharedSuccess", {
          name,
          email,
        }),
      );
      setShareDialogOpen(false);
    } catch (error) {
      console.error("Error sharing collection:", error);

      // Check for specific error message
      if (error.message.includes("User not found or inactive")) {
        setSharingError(t("aiCatalog.userNotFoundError"));
      } else {
        setSharingError(error.message || t("collections.failedToShare"));
      }
    } finally {
      setIsSharing(false);
    }
  };

  // Check if user has permission to delete the collection
  const canDelete = access_level === "admin";

  // Check if user has permission to share the collection
  const canShare = access_level === "admin";

  // Access level styling and icons
  const getAccessLevelInfo = () => {
    switch (access_level) {
      case "admin":
        return {
          label: t("aiCatalog.admin"),
          icon: <AdminIcon fontSize="small" />,
          color: "primary",
        };
      case "modifier":
        return {
          label: t("aiCatalog.editor"),
          icon: <EditIcon fontSize="small" />,
          color: "success",
        };
      case "viewer":
        return {
          label: t("aiCatalog.viewer"),
          icon: <VisibilityIcon fontSize="small" />,
          color: "default",
        };
      default:
        return {
          label: t("projects.accessLevel"),
          icon: <VisibilityIcon fontSize="small" />,
          color: "default",
        };
    }
  };

  const accessInfo = getAccessLevelInfo();

  return (
    <>
      <Card
        sx={{
          cursor: "pointer",
          transition: "transform 0.2s, box-shadow 0.2s",
          "&:hover": {
            transform: "translateY(-5px)",
            boxShadow: "0 10px 20px rgba(0,0,0,0.2)",
          },
          height: "auto", // Allow dynamic height
          minHeight: 160,
          display: "flex",
          flexDirection: "column",
          position: "relative", // For positioning the shared indicator
          overflow: "visible", // Allow elements to overflow for visual effects
        }}
        onClick={() => onClick(id)}
      >
        {/* Shared indicator */}
        {is_shared && (
          <Box
            sx={{
              position: "absolute",
              top: -8,
              insetInlineEnd: 16,
              transform: "rotate(0)",
              zIndex: 1,
            }}
          >
            <Chip
              icon={<ShareIcon fontSize="small" />}
              label={t("aiCatalog.shared")}
              size="small"
              color="primary"
              variant="outlined"
              sx={{
                fontWeight: "bold",
                border: "1px solid",
                boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
              }}
            />
          </Box>
        )}

        <CardContent
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            p: 2,
            pt: is_shared ? 3 : 2, // Give more top padding if shared to avoid overlap
          }}
        >
          <Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                mb: 2,
              }}
            >
              <Tooltip title={name} placement="top" arrow>
                <Typography
                  variant="h6"
                  component="h3"
                  sx={{
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    maxWidth: "calc(100% - 60px)", // Leave space for icons
                    fontSize: "1.1rem", // Slightly smaller font size
                  }}
                >
                  {name}
                </Typography>
              </Tooltip>
              <Box
                sx={{ display: "flex", alignItems: "center", flexShrink: 0 }}
              >
                <LibraryBooksIcon color="primary" sx={{ marginInlineEnd: 1 }} />
                <IconButton
                  size="small"
                  onClick={handleMenuOpen}
                  aria-label={t("common.actions")}
                  aria-controls={open ? "collection-menu" : undefined}
                  aria-haspopup="true"
                  aria-expanded={open ? "true" : undefined}
                >
                  <MoreVertIcon />
                </IconButton>
                <Menu
                  id="collection-menu"
                  anchorEl={anchorEl}
                  open={open}
                  onClose={handleMenuClose}
                  onClick={(e) => e.stopPropagation()}
                >
                  {canShare && (
                    <MenuItem onClick={handleShareClick}>
                      <ShareIcon fontSize="small" sx={{ marginInlineEnd: 1 }} />
                      {t("common.share")}
                    </MenuItem>
                  )}
                  <MenuItem
                    onClick={handleDelete}
                    sx={{ color: "error.main" }}
                    disabled={!canDelete}
                  >
                    <DeleteIcon
                      fontSize="small"
                      sx={{ marginInlineEnd: 1 }}
                      color="error"
                    />
                    {t("common.delete")}
                  </MenuItem>
                </Menu>
              </Box>
            </Box>

            {description && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  mb: 2,
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  maxHeight: "2.5em", // Slightly reduced
                  fontSize: "0.875rem", // Ensure consistent font size
                }}
              >
                {description}
              </Typography>
            )}
          </Box>

          <Box sx={{ mt: "auto" }}>
            {/* Access level indicator */}
            {is_shared && (
              <Box sx={{ mb: 1.5 }}>
                <Chip
                  avatar={
                    <Avatar sx={{ bgcolor: "transparent" }}>
                      {accessInfo.icon}
                    </Avatar>
                  }
                  label={accessInfo.label}
                  size="small"
                  color={accessInfo.color}
                  variant="outlined"
                  sx={{ height: 24 }}
                />
              </Box>
            )}

            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontSize: "0.8rem" }}
              >
                {t("mentions.collectionCount", {
                  count: document_count || 0,
                })}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontSize: "0.8rem" }}
              >
                {t("common.created")} {formatDate(created_at)}
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Share Collection Dialog */}
      <ShareDialog
        open={shareDialogOpen}
        onClose={handleShareDialogClose}
        onSubmit={handleShareSubmit}
        title={t("collections.shareCollectionTitle", { name })}
        itemType="Collection"
        isSubmitting={isSharing}
        error={sharingError}
        projectId={project_id}
        name={name}
      />
    </>
  );
};

export default CollectionCard;
